import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import CoverflowGallery from './originkit/coverflowgallery.tsx';

const SLOT_PREFIX = 'aidb-coverflow:';
const DEFAULT_SETTINGS = {
  cardWidth: 300,
  cardHeight: 340,
  radius: 3.4,
  tilt: 0,
  sideTilt: 4,
  gap: 2,
  opacity: 80,
  inactiveCardsPerSide: 1,
  duration: 0.6,
  ease: 'smooth',
};
const EASES = {
  smooth: [0.22, 1, 0.36, 1],
  linear: 'linear',
  easeIn: 'easeIn',
  easeOut: 'easeOut',
  easeInOut: 'easeInOut',
};

/*
 * Originkit Coverflow Gallery 业务适配层。
 * 原组件只接收图片 slide；这里把现有 React 图表通过 Portal 注入其卡槽，
 * 透视、层级、可见卡数和切换仍由 Originkit 组件负责。
 */
const TvdLiteCoverflow = forwardRef(function TvdLiteCoverflow({
  items,
  initialIndex = 0,
  onChange,
  reducedMotion = false,
  settings,
}, ref) {
  const config = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  const moveMs = reducedMotion ? 60 : Math.max(50, config.duration * 1000);
  const rootRef = useRef(null);
  const activeRef = useRef(Math.max(0, Math.min(items.length - 1, initialIndex)));
  const lockRef = useRef(false);
  const lockTimerRef = useRef(null);
  const pendingRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(activeRef.current);
  const [slots, setSlots] = useState([]);
  const itemIds = items.map((item) => item.id);
  const itemKey = itemIds.join('|');

  const slides = useMemo(() => itemIds.map((id) => ({
    title: `${SLOT_PREFIX}${id}`,
  })), [itemKey]);

  const findSlots = useCallback(() => {
    const root = rootRef.current;
    if (!root) return [];
    return itemIds.map((id) => root.querySelector(`[aria-label="${SLOT_PREFIX}${id}"]`));
  }, [itemKey]);

  const commitIndex = useCallback((nextIndex) => {
    if (!items.length) return;
    const next = Math.max(0, Math.min(items.length - 1, nextIndex));
    if (next === activeRef.current) return;
    activeRef.current = next;
    setActiveIndex(next);
    onChange?.(next);
  }, [items.length, onChange]);

  const beginLock = useCallback(() => {
    lockRef.current = true;
    if (lockTimerRef.current) window.clearTimeout(lockTimerRef.current);
    lockTimerRef.current = window.setTimeout(() => {
      lockRef.current = false;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending != null && pending !== activeRef.current) {
        const nextSlots = findSlots();
        nextSlots[pending]?.click();
      }
    }, moveMs);
  }, [findSlots, moveMs]);

  const handleActiveChange = useCallback((nextIndex) => {
    commitIndex(nextIndex);
    beginLock();
  }, [beginLock, commitIndex]);

  const goTo = useCallback((nextIndex) => {
    if (!items.length) return;
    const next = Math.max(0, Math.min(items.length - 1, nextIndex));
    if (next === activeRef.current) return;
    if (lockRef.current) {
      pendingRef.current = next;
      return;
    }
    const nextSlots = findSlots();
    nextSlots[next]?.click();
  }, [findSlots, items.length]);

  const step = useCallback((direction) => {
    goTo(activeRef.current + direction);
  }, [goTo]);

  useImperativeHandle(ref, () => ({
    goTo,
    step,
    getIndex: () => activeRef.current,
  }), [goTo, step]);

  useLayoutEffect(() => {
    const nextSlots = findSlots();
    setSlots(nextSlots);
    return () => {
      if (lockTimerRef.current) window.clearTimeout(lockTimerRef.current);
    };
  }, [findSlots]);

  return (
    <div
      className="tvl-coverflow-stage"
      ref={rootRef}
      style={{
        '--tvl-inactive-opacity': config.opacity / 100,
        '--tvl-transition-duration': `${reducedMotion ? 0 : config.duration}s`,
      }}
    >
      <CoverflowGallery
        key={itemKey}
        slides={slides}
        initialIndex={initialIndex}
        onActiveChange={handleActiveChange}
        cardWidth={config.cardWidth}
        cardHeight={config.cardHeight}
        radius={config.radius}
        tilt={config.tilt}
        sideTilt={config.sideTilt}
        gap={config.gap}
        opacity={config.opacity}
        inactiveCardsPerSide={config.inactiveCardsPerSide}
        autoplay={false}
        transition={{
          type: 'tween',
          duration: reducedMotion ? 0 : config.duration,
          delay: 2.5,
          ease: EASES[config.ease] || EASES.smooth,
        }}
        showTitle={false}
      />
      {items.map((item, index) => slots[index] ? createPortal(
        <div
          className="tvl-coverflow-portal"
          data-active={index === activeIndex ? 'true' : 'false'}
          onClick={(event) => {
            if (event.target.closest?.('button, a, input, select, textarea')) event.stopPropagation();
          }}
        >
          {item.content}
        </div>,
        slots[index],
        item.id,
      ) : null)}
    </div>
  );
});

export default TvdLiteCoverflow;

/* Coverflow 临时参数工作台：隐藏入口为 5 秒内连续点击段标题 5 次（由挂载方接线）；
   保存值只写当前浏览器，便于评审快速试参，不改变报告数据与深链。
   详情页当前不挂载 Coverflow（组件与样式保留待复用），工作台随本模块一并保留。 */
export const TVL_COVERFLOW_PREF_KEY = 'aidb:tvl-coverflow:v4';
export const TVL_COVERFLOW_DEFAULTS = Object.freeze({ ...DEFAULT_SETTINGS });
export const TVL_COVERFLOW_LIMITS = {
  cardWidth: [240, 320], cardHeight: [240, 340], radius: [0, 20],
  tilt: [-20, 20], sideTilt: [-20, 20], gap: [0, 12],
  opacity: [0, 100], inactiveCardsPerSide: [0, 3], duration: [0, 1.5],
};
export const TVL_COVERFLOW_EASES = ['smooth', 'linear', 'easeIn', 'easeOut', 'easeInOut'];
export const tvlCoverflowClean = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {};
  const next = { ...TVL_COVERFLOW_DEFAULTS };
  Object.entries(TVL_COVERFLOW_LIMITS).forEach(([key, [min, max]]) => {
    const n = Number(source[key]);
    if (Number.isFinite(n)) next[key] = Math.max(min, Math.min(max, n));
  });
  next.inactiveCardsPerSide = Math.round(next.inactiveCardsPerSide);
  if (TVL_COVERFLOW_EASES.includes(source.ease)) next.ease = source.ease;
  return next;
};
export const tvlCoverflowLoad = () => {
  try {
    const raw = window.localStorage.getItem(TVL_COVERFLOW_PREF_KEY);
    return raw ? tvlCoverflowClean(JSON.parse(raw)) : { ...TVL_COVERFLOW_DEFAULTS };
  } catch (_) {
    return { ...TVL_COVERFLOW_DEFAULTS };
  }
};

export function TvdCoverflowTuner({ value, onChange, onCancel, onSave }) {
  const controls = [
    ['cardWidth', '卡片宽度', 'Card width', 240, 320, 2, 'px'],
    ['cardHeight', '卡片高度', 'Card height', 240, 340, 2, 'px'],
    ['radius', '圆角', 'Radius', 0, 20, .2, ''],
    ['tilt', '纵向倾斜', 'Tilt', -20, 20, 1, '°'],
    ['sideTilt', '侧向倾斜', 'Sideways tilt', -20, 20, 1, '°'],
    ['gap', '卡片间距', 'Gap', 0, 12, .5, ''],
    ['opacity', '非激活透明度', 'Inactive opacity', 0, 100, 5, '%'],
    ['inactiveCardsPerSide', '非激活卡片单侧数量', 'Inactive cards per side', 0, 3, 1, ''],
    ['duration', '动画时长', 'Transition duration', 0, 1.5, .05, 's'],
  ];
  return (
    <div className="tvl-tuner-mask" role="presentation" onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <section className="tvl-tuner" role="dialog" aria-modal="true" aria-labelledby="tvl-tuner-title">
        <header>
          <div>
            <b id="tvl-tuner-title">Coverflow 参数</b>
            <small>实时预览 · 保存后在当前浏览器持续生效</small>
          </div>
          <button type="button" aria-label="关闭参数浮窗" onClick={onCancel}>×</button>
        </header>
        <div className="tvl-tuner-body">
          {controls.map(([key, label, en, min, max, step, unit]) => (
            <label className="tvl-tuner-row" key={key}>
              <span><b>{label}</b><small>{en}</small></span>
              <output>{value[key]}{unit}</output>
              <input type="range" min={min} max={max} step={step} value={value[key]}
                aria-label={label} onChange={(e) => onChange({ ...value, [key]: Number(e.target.value) })} />
            </label>
          ))}
          <label className="tvl-tuner-select">
            <span><b>缓动曲线</b><small>Transition ease</small></span>
            <select value={value.ease} aria-label="缓动曲线"
              onChange={(e) => onChange({ ...value, ease: e.target.value })}>
              <option value="smooth">Smooth</option>
              <option value="linear">Linear</option>
              <option value="easeIn">Ease in</option>
              <option value="easeOut">Ease out</option>
              <option value="easeInOut">Ease in-out</option>
            </select>
          </label>
        </div>
        <footer>
          <button type="button" className="reset" onClick={() => onChange({ ...TVL_COVERFLOW_DEFAULTS })}>恢复默认</button>
          <button type="button" onClick={onCancel}>取消</button>
          <button type="button" className="save" onClick={onSave}>保存参数</button>
        </footer>
      </section>
    </div>
  );
}
